import { useCallback, useState } from 'react';
import { VisibilityLayers } from '../../../../../types/enums/VisibilityLayers';
import { WithConfigurationCanvas } from '../../../configuration-canvas/WithConfigurationCanvas';
import { VisibilityElementLayersSelector } from '../../../configuration-canvas/types';
import { CanvasUnitedFlow } from './CanvasUnitedFlow';
import {
  CanvasPillTypes,
  GroupLabelEntityType,
  GroupPillEntityType,
  NodeEntityTypes,
  StepEntityType,
  StepPillEntityType,
  SweepCanvasEntities,
} from '../../../../sweep-canvas/canvas-types';
import { usePanels } from '../../../../panels/usePanels';
import { documentationPanelType } from '../../../../documentation/types';
import { Box } from '@mui/material';
import { DEFAULT_LAYERS_ID } from '../../../../../reducers/canvasLayersReducer';
import useAutomationsCanvasInteractions from '../../useAutomationsCanvasInteractions';
import { automationsPanelType, playbookAlertsPanelType } from '../../../../Automations/panelTypes';
import { useConfigurationCanvas } from '../../../configuration-canvas/public/useConfigurationCanvas';
import { useSelector } from 'react-redux';
import { selectStageDialogStage } from '../../../../../reducers/funnel-flow-page/stageDialogReducer';
import { funnelPanelType } from '../../../funnel-map-flow/dialogs/funnel-panel/funnelPanelTypes';
import { gatePanelType } from '../../../funnel-map-flow/dialogs/gates/gatePanelType';
import { useDocumentationCanvasInteractions } from '../../../../documentation/useCanvasDocumentationInteractions';
import { useCanvasFilters } from '../../../configuration-canvas-filters/useCanvasFilters';
import { useInitializeObjectsStructure } from '../../../../documentation/hooks/useInitializeObjectsStructure';
import useObjectTypesWithFetch from '../../../../../hooks/useObjectTypesWithFetch';
import { useCloseAgentCenterFirst } from '../../../../aiAgentCenter/useCloseAgentCenterFirst';
import { useUnitedSweepCanvasReorderCtx } from '../UnitedSweepCanvasReorderCtx';
import { useFeatureToggle } from '../../../../common/useFeatureToggle';
import useOpenStepDialog from '../useOpenStageDialog';

const visibilityElementsLayerSelector: VisibilityElementLayersSelector = {
  type: 'layersSelector',
  layersId: DEFAULT_LAYERS_ID,
  visibilityLayersSelectors: {
    [VisibilityLayers.SWEEP_AUTOMATIONS]: true,
    [VisibilityLayers.NURTURING_STEPS]: true,
    [VisibilityLayers.COMMENTS]: true,
    [VisibilityLayers.VALIDATION_RULES]: true,
    [VisibilityLayers.SFDC_AUTOMATIONS]: true,
    [VisibilityLayers.APEX]: true,
    [VisibilityLayers.PLAYBOOK_ALERTS]: true,
    [VisibilityLayers.ASSIGNMENT_RULES]: true,
    [VisibilityLayers.FUNNEL_MATCHING_AND_DEDUPLICATION]: true,
    [VisibilityLayers.GATES]: true,
  },
  defaultVisibilityMap: {
    [VisibilityLayers.NURTURING_STEPS]: true,
    [VisibilityLayers.COMMENTS]: true,
    [VisibilityLayers.SFDC_AUTOMATIONS]: true,
    [VisibilityLayers.APEX]: true,
    [VisibilityLayers.VALIDATION_RULES]: true,
    [VisibilityLayers.GATES]: true,
  },
};

const documentationPills = [
  CanvasPillTypes.apexTrigger,
  CanvasPillTypes.groupApex,
  CanvasPillTypes.sfdcAutomation,
  CanvasPillTypes.groupSfdcAutomation,
  CanvasPillTypes.validationRule,
  CanvasPillTypes.groupValidationRule,
  CanvasPillTypes.groupDeduplication,
  CanvasPillTypes.groupMatching,
  CanvasPillTypes.assignmentRule,
  CanvasPillTypes.groupAssignmentRule,
  CanvasPillTypes.scheduledAssignment,
  CanvasPillTypes.groupScheduledAssignment,
];

const automationPills = [CanvasPillTypes.automation, CanvasPillTypes.groupAutomation];
const alertsPills = [CanvasPillTypes.playbookAlert, CanvasPillTypes.groupPlaybookAlert];

const isDocumentationPill = (entity: SweepCanvasEntities) => {
  if ('canvasPillType' in entity) {
    return documentationPills.includes(entity.canvasPillType);
  }
  return false;
};

const isAutomationsPill = (entity: SweepCanvasEntities) => {
  if ('canvasPillType' in entity) {
    return automationPills.includes(entity.canvasPillType);
  }
  return false;
};

const isAlertsPill = (entity: SweepCanvasEntities) => {
  if ('canvasPillType' in entity) {
    return alertsPills.includes(entity.canvasPillType);
  }
  return false;
};

const useOpenFunnelDialog = () => {
  const { maybeOpenPanel } = usePanels();
  const closeAgentCenterFirst = useCloseAgentCenterFirst();

  const openFunnelDialog = useCallback(
    ({ funnelId }: { funnelId: string }) => {
      closeAgentCenterFirst(
        maybeOpenPanel({
          panelType: funnelPanelType,
          data: {
            funnelId,
          },
        }),
      );
    },
    [closeAgentCenterFirst, maybeOpenPanel],
  );
  return {
    openFunnelDialog,
  };
};

export const CanvasUnitedConfigurationPage = () => {
  const { maybeOpenPanel, maybeCloseActivePanel } = usePanels();
  const { canvasCrmOrgId, canvasFunnelMap } = useConfigurationCanvas();
  const { setFilterValues, focusOnCanvasElements, clearAllValues } = useCanvasFilters();

  const { objectTypes } = useObjectTypesWithFetch({ crmOrgId: canvasCrmOrgId });
  const { openStepDialog } = useOpenStepDialog();
  const { openFunnelDialog } = useOpenFunnelDialog();
  const { onEntityClick: onAutomationsEntityClick } = useAutomationsCanvasInteractions();
  const { onDocumentationEntityClick } = useDocumentationCanvasInteractions({
    focusOnCanvasElements,
    setFilterValues,
    onSweepElementClick: onAutomationsEntityClick,
    crmOrgId: canvasCrmOrgId,
    clearAllValues,
  });

  //required for documentation filters
  useInitializeObjectsStructure({
    funnelsData: canvasFunnelMap.funnelsData,
    recordTypesData: canvasFunnelMap.recordTypesData,
    crmOrgId: canvasCrmOrgId,
    objectTypes,
  });

  const [selectedGateId, setSelectedGateId] = useState<string>();
  const { setReorderStepsMode } = useUnitedSweepCanvasReorderCtx();
  const { canvasReorderSteps: canvasReorderStepsFT } = useFeatureToggle();

  const onEntityClick = useCallback(
    ({
      entity,
      event,
      funnelId,
      stepId,
      isLongClick,
    }: {
      stepId?: string;
      funnelId: string;
      entity: StepEntityType | StepPillEntityType | GroupPillEntityType | GroupLabelEntityType;
      event: React.MouseEvent | React.TouchEvent;
      isLongClick?: boolean;
    }) => {
      if (entity.type === NodeEntityTypes.Step && stepId && funnelId) {
        if (isLongClick && canvasReorderStepsFT) {
          maybeCloseActivePanel({
            onCloseConfirm: () => {
              setReorderStepsMode({
                stepId,
                groupId: funnelId,
              });
            },
          });
          return;
        } else {
          openStepDialog({ stepId, funnelId });
        }
      }

      if (entity.type === NodeEntityTypes.GroupLabel) {
        openFunnelDialog({ funnelId });
      }
      if (isDocumentationPill(entity)) {
        maybeOpenPanel({
          panelType: documentationPanelType,
          onOpen: () => {
            onDocumentationEntityClick({
              stepId: stepId,
              funnelId: funnelId,
              entity: entity,
              event: event,
              funnelsData: canvasFunnelMap.funnelsData,
              recordTypesData: canvasFunnelMap.recordTypesData,
            });
          },
        });
      }
      if (isAutomationsPill(entity)) {
        maybeOpenPanel({
          panelType: automationsPanelType,
          onOpen: () => {
            setTimeout(() => {
              onAutomationsEntityClick({
                stepId: stepId,
                funnelId: funnelId,
                entity: entity,
                event: event,
              });
            }, 0); // Quick fix. TODO: pass this as arguments to the panel
          },
        });
      }
      if (isAlertsPill(entity)) {
        maybeOpenPanel({
          panelType: playbookAlertsPanelType,
          onOpen: () => {
            setTimeout(() => {
              onAutomationsEntityClick({
                stepId: stepId,
                funnelId: funnelId,
                entity: entity,
                event: event,
              });
            }, 0);
          },
        });
      }
    },
    [
      canvasReorderStepsFT,
      maybeCloseActivePanel,
      setReorderStepsMode,
      openStepDialog,
      openFunnelDialog,
      maybeOpenPanel,
      onDocumentationEntityClick,
      canvasFunnelMap.funnelsData,
      canvasFunnelMap.recordTypesData,
      onAutomationsEntityClick,
    ],
  );

  const onStageGateClick = useCallback(
    (funnelId: string, stageId: string, exitCriteriaId: string) => {
      maybeOpenPanel({
        panelType: gatePanelType,
        onOpen: () => {
          setSelectedGateId(exitCriteriaId);
        },
        onClose: () => {
          setSelectedGateId(undefined);
        },
        data: {
          funnelId,
          stageId,
          exitCriteriaId,
        },
      });
    },
    [maybeOpenPanel],
  );

  const selectedStageId = useSelector(selectStageDialogStage)?._stageId;
  const { isPanelOpen, getPanelDataIfOpened } = usePanels();
  const _selectedGateId = isPanelOpen(gatePanelType) ? selectedGateId : undefined;
  const _selectedFunnelId = getPanelDataIfOpened(funnelPanelType)?.funnelId;

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100%' }}>
      <WithConfigurationCanvas
        visibilityElements={visibilityElementsLayerSelector}
        onFunnelStepClick={onEntityClick}
        onFunnelLabelClick={onEntityClick}
        onFunnelLabelPillClick={onEntityClick}
        onStageGateClick={onStageGateClick}
        selectedGateId={_selectedGateId}
        selectedStageId={selectedStageId}
        selectedFunnelId={_selectedFunnelId}
      >
        <CanvasUnitedFlow />
      </WithConfigurationCanvas>
    </Box>
  );
};
